import type { JSX, Ref } from 'react'
import type { PopupDirection } from 'blocks/Popup'
import type { AuthCodeRef } from './PINCodeInput.h'

import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { TextBoxMaxLine1Caption4 } from 'atoms/TextBox/Templates'
import { COLOR, FIXED_INDENTS } from 'vars/index'
import { useHandlers, useUpdateFirstField } from './handlers'
import { PINCodeField } from './PINCodeField'
import {
  ErrorLayoutSC,
  PINCodeInputSC,
  PINCodeInputWrapper,
} from './PINCodeInput.styled'

const CHANGE_TYPE_INPUT_DELAY = 500

export type IPINCodeInput = {
  ref?: Ref<AuthCodeRef>
  autoFocus?: boolean
  disabled?: boolean
  lengthPinCode?: number
  onChange?: (res: string) => void
  paddings?: FIXED_INDENTS[]
  columnGap?: FIXED_INDENTS
  rowGap?: FIXED_INDENTS
  direction?: PopupDirection
  invalid?: boolean
  errorMessage?: string
  colorDefault?: COLOR
  colorDisabled?: COLOR
  colorError?: COLOR
  onComplete?: (value: string) => void
  values?: string[]
  type?: string
  valueAfterInvalid?: string
}

export function PINCodeInput({
  ref,
  autoFocus = true,
  disabled,
  lengthPinCode = 4,
  onChange,
  paddings,
  direction,
  columnGap = FIXED_INDENTS.INDENT_50,
  invalid,
  errorMessage,
  rowGap = FIXED_INDENTS.INDENT_40,
  colorDefault = COLOR.TEXT_ICON_SECONDARY,
  colorDisabled = COLOR.TEXT_ICON_SECONDARY,
  colorError = COLOR.ADDITIONAL_RED_2,
  onComplete,
  valueAfterInvalid = '',
}: IPINCodeInput): JSX.Element {
  const submitFormTimeoutId = useRef<ReturnType<typeof setTimeout> | null>(null)
  const inputsRef = useRef<Array<HTMLInputElement>>([])
  const [isFocused, setIsFocused] = useState(autoFocus)

  const sendResult = useCallback(() => {
    const result = inputsRef.current.map((input) => input.value).join('')
    if (onChange) {
      onChange(result)
      if (submitFormTimeoutId.current) clearTimeout(submitFormTimeoutId.current)
    }
    if (result.length === lengthPinCode && onComplete) {
      submitFormTimeoutId.current = setTimeout(
        () => onComplete(result),
        CHANGE_TYPE_INPUT_DELAY
      )
    }
  }, [])

  useImperativeHandle(ref, () => ({
    focus: () => {
      if (inputsRef.current) {
        inputsRef.current[0].focus()
      }
    },
    clear: () => {
      if (inputsRef.current) {
        for (let i = 0; i < inputsRef.current.length; i++) {
          inputsRef.current[i].value = ''
        }
        inputsRef.current[0].focus()
      }
      sendResult()
    },
  }))

  useEffect(() => {
    if (autoFocus) {
      inputsRef.current[0].focus()
    }
  }, [])

  const { handleOnKeyDown, handleOnChange, handleOnPaste, handleOnFocus } =
    useHandlers({
      sendResult,
      inputsRef,
      setIsFocused,
      lengthPinCode,
    })

  useEffect(() => {
    if (invalid) setIsFocused(true)
  }, [invalid])

  useUpdateFirstField({ valueAfterInvalid, inputsRef, setIsFocused })

  const isMessageError = Boolean(errorMessage) && (invalid || disabled)
  const inputs: any[] = []
  for (let i = 0; i < lengthPinCode; i++) {
    inputs.push(
      <PINCodeField
        key={i}
        onChange={handleOnChange}
        onKeyDown={handleOnKeyDown}
        onFocus={handleOnFocus}
        onPaste={handleOnPaste}
        ref={(el: HTMLInputElement) => {
          inputsRef.current[i] = el
        }}
        autoComplete={i === 0 ? 'one-time-code' : 'off'}
        disabled={disabled}
        indexField={i}
        invalid={invalid}
        timeoutDelay={CHANGE_TYPE_INPUT_DELAY}
        autoFocus={isFocused ? i === 0 : false}
        refs={inputsRef}
      />
    )
  }

  return (
    <PINCodeInputSC
      direction={direction}
      rowGap={rowGap}
      colorDefault={colorDefault}
      colorDisabled={colorDisabled}
      colorError={colorError}
      invalid={invalid}
      disabled={disabled}
    >
      <PINCodeInputWrapper paddings={paddings} columnGap={columnGap}>
        {inputs}
      </PINCodeInputWrapper>
      <ErrorLayoutSC>
        {isMessageError && <TextBoxMaxLine1Caption4 title={errorMessage} />}
      </ErrorLayoutSC>
    </PINCodeInputSC>
  )
}
